import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ContexteToast } from './toast-context';

/**
 * Message transitoire de la maquette validée (`toast()` dans
 * `docs/maquette/template.html`), rendu en composant plutôt qu'en dialogue
 * natif : `alert()` bloque la page et ne se met pas à la charte.
 *
 * La bulle est annoncée aux lecteurs d'écran par une région `aria-live` qui
 * existe en permanence — une région ajoutée au moment du message n'est pas
 * relue par tous les lecteurs. Elle disparaît d'elle-même après 2,2 s, comme
 * dans la maquette, et ne capte jamais le focus.
 */

interface Bulle {
  id: number;
  texte: string;
}

const DUREE_MS = 2200;

export function FournisseurToasts({ children }: { children: ReactNode }): JSX.Element {
  const [bulles, setBulles] = useState<Bulle[]>([]);
  const compteur = useRef(0);
  const minuteries = useRef(new Set<ReturnType<typeof setTimeout>>());

  const notifier = useCallback((texte: string) => {
    const id = ++compteur.current;
    setBulles((actuelles) => [...actuelles, { id, texte }]);
    const minuterie = setTimeout(() => {
      minuteries.current.delete(minuterie);
      setBulles((actuelles) => actuelles.filter((bulle) => bulle.id !== id));
    }, DUREE_MS);
    minuteries.current.add(minuterie);
  }, []);

  // Sans ce nettoyage, un message en cours au démontage réveillerait un
  // `setState` sur un composant disparu — et en développement, le double
  // montage de React laisserait une minuterie orpheline.
  useEffect(() => {
    const enCours = minuteries.current;
    return () => {
      enCours.forEach((minuterie) => clearTimeout(minuterie));
      enCours.clear();
    };
  }, []);

  return (
    <ContexteToast.Provider value={notifier}>
      {children}
      {/* Ancrée au-dessus du bouton flottant d'accessibilité, et sur toute la
          largeur moins les gouttières sur mobile pour qu'un message long ne
          sorte pas de l'écran. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-6 z-[120] flex flex-col items-end gap-2 sm:inset-x-auto sm:right-6"
      >
        {bulles.map((bulle) => (
          <div key={bulle.id} className="toast-bulle max-w-full text-sm sm:max-w-md">
            {bulle.texte}
          </div>
        ))}
      </div>
    </ContexteToast.Provider>
  );
}
